import { useState } from 'react'
import { AppIcons, NavIcon, StockTaggerLogoBlack } from './icons'
import { AccountDropdown } from './AccountDropdown'

interface Tab {
  id: 'upload' | 'metadata' | 'export' | 'pricing' | 'account'
  name: string
  icon: any
}

interface MobileNavigationProps {
  tabs: Tab[]
  activeTab: string
  onTabChange: (tabId: string) => void
  user: any
  currentImages: any[]
  imageMetadata: Record<string, any>
  isBatchGenerating: boolean
  batchProgress: { current: number; total: number }
  onNavigateToAccount?: (page: 'subscription' | 'settings' | 'security' | 'help') => void
}

export function MobileNavigation({
  tabs,
  activeTab,
  onTabChange,
  user,
  currentImages,
  imageMetadata,
  isBatchGenerating,
  batchProgress,
  onNavigateToAccount
}: MobileNavigationProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen)

  return (
    <>
      {/* Mobile Header */}
      <header className="lg:hidden bg-white shadow-sm border-b sticky top-0 z-40">
        <div className="px-4 py-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <button
                onClick={toggleMenu}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Toggle menu"
              >
                <AppIcons.Menu className="w-6 h-6 text-gray-700" />
              </button>
              <StockTaggerLogoBlack className="w-8 h-8" />
              <div>
                <h1 className="text-lg font-bold text-gray-900">StockTagger.ai</h1>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {/* Status Badges - Compact for Mobile */}
              {currentImages.length > 0 && (
                <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded-full">
                  {currentImages.length}
                </span>
              )}
              {Object.keys(imageMetadata).length > 0 && (
                <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                  {Object.keys(imageMetadata).length}
                </span>
              )}
              {isBatchGenerating && (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
                </div>
              )}

              {/* Account Dropdown - Same as Desktop */}
              {user && (
                <AccountDropdown onNavigateToAccount={onNavigateToAccount} />
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black bg-opacity-50" onClick={toggleMenu}>
          <div className="fixed inset-y-0 left-0 w-72 bg-white shadow-lg" onClick={(e) => e.stopPropagation()}>
            {/* Menu Header */}
            <div className="p-4 border-b">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900">Navigation</h2>
                <button
                  onClick={toggleMenu}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <AppIcons.X className="w-5 h-5 text-gray-700" />
                </button>
              </div>
            </div>
            
            {/* Menu Items */}
            <nav className="p-4">
              <div className="space-y-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => {
                      onTabChange(tab.id)
                      setIsMenuOpen(false)
                    }}
                    className={`w-full flex items-center px-4 py-3 rounded-lg text-left transition-colors ${
                      activeTab === tab.id
                        ? 'bg-blue-50 text-blue-600 border border-blue-200'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <NavIcon icon={tab.icon} className="w-5 h-5 mr-3" />
                    <span className="font-medium">{tab.name}</span>
                    {activeTab === tab.id && (
                      <AppIcons.CheckCircle className="w-4 h-4 ml-auto text-blue-600" />
                    )}
                  </button>
                ))}
              </div>
              
              {/* User Account Info in Menu */}
              {user && onNavigateToAccount && (
                <div className="mt-6 pt-6 border-t">
                  <div className="px-4 py-2 text-xs text-gray-500 uppercase tracking-wide font-semibold">
                    Account
                  </div>
                  <button
                    onClick={() => {
                      onNavigateToAccount('settings')
                      setIsMenuOpen(false)
                    }}
                    className="w-full flex items-center px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <AppIcons.User className="w-5 h-5 mr-3" />
                    <div className="text-left flex-1">
                      <div className="font-medium">
                        {user.user_metadata?.full_name || user.email?.split('@')[0] || 'User'}
                      </div>
                      <div className="text-sm text-gray-500">Account Settings</div>
                    </div>
                  </button>
                </div>
              )}
            </nav>
          </div>
        </div>
      )}

      {/* Mobile Tab Bar - Alternative bottom navigation */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-30">
        <div className="flex">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`flex-1 py-3 px-2 flex flex-col items-center transition-colors ${
                activeTab === tab.id
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <NavIcon icon={tab.icon} className="w-5 h-5 mb-1" />
              <span className="text-xs font-medium">{tab.name}</span>
            </button>
          ))}
        </div>
      </div>
    </>
  )
}